import { play } from 'cuelume'

import { buildAdvancedTwapAlgoParams } from '@/features/advanced-twap/lib/build-advanced-twap-params'
import {
  resolveDenomination,
  resolveEffectiveAmount,
} from '@/features/order/order-form/lib/instrument-resolution'
import { baseCurrencyHint, dedupeTargets } from '@/features/order/order-form/lib/submitters/shared'
import type { SubmitterContext } from '@/features/order/order-form/lib/submitters/types'
import { trackOrderItems } from '@/lib/analytics-order'
import { toast } from '@/lib/toast'
import { randomUUID, safeParseFloat } from '@/lib/utils'
import { resolveQtyStep } from '@/lib/utils/qty-precision'
import type { useStartAdvancedTwapAlgo } from '@/service/algo.hook'
import type { Instrument } from '@/types/generated/common/types'

export type SubmitAdvancedTwapDeps = {
  instrumentMap: Map<string, Instrument>
  startAdvancedTwap: ReturnType<typeof useStartAdvancedTwapAlgo>['mutateAsync']
}

// Submit Advanced TWAP order. Single instrument, single account (contract: "Single
// instrument, single account") — unlike submitTwapOrder, this never batches across
// multiple submission rows: a shared catchup order size can't correctly apply to rows
// with different amountUnits, and the wire params have no way to express more than one
// run per submit anyway. order-form-summary.tsx's isFormValid already disables the
// submit button when more than one row would resolve (isSplitAccountRow / multi-leg);
// this is the defensive second check, mirroring submitTwapOrder's own
// `rows.length === 0` guard.
export async function submitAdvancedTwapOrder(ctx: SubmitterContext, deps: SubmitAdvancedTwapDeps) {
  const { t } = ctx
  const { instrumentMap, startAdvancedTwap } = deps
  if (!ctx.validateSubmissionRows()) return
  const rawValues = ctx.getRawValues()
  const rows = ctx.getSubmissionRows()

  if (rows.length === 0) {
    toast.error(t('selectAccountFirst'), { position: 'bottom-right' })
    return
  }
  if (rows.length > 1) {
    toast.error(t('advancedTwapSingleAccountOnly'), { position: 'bottom-right' })
    return
  }

  ctx.setIsSubmitting(true)

  try {
    const { item, rawItem, account } = rows[0]
    const amount = rawItem.amount
    if (!amount || amount <= 0) {
      throw new Error(t('enterValidAmount'))
    }

    // Options are sized in contracts (BASE) whatever the row's amountUnit says, and a
    // contract count is scaled to raw quantity like every other submitter does.
    const denomination = resolveDenomination(
      instrumentMap,
      item.symbol,
      item.itemType,
      item.amountUnit,
      baseCurrencyHint(item.baseCurrency)
    )
    const effectiveAmount = resolveEffectiveAmount(
      instrumentMap,
      item.symbol,
      item.itemType,
      amount
    )

    // qtyStep/minOrderSize only round the auto-derived catchup clip size to a valid
    // order (see derive-catchup-params.ts) — approximate is fine since ceiling to a
    // step only ever rounds the clip size up, never below the required minimum.
    const instrument = instrumentMap.get(item.symbol)
    const qtyStep = resolveQtyStep(instrument?.qtyPrecision)
    const minOrderSize = instrument?.minTradeAmount
      ? safeParseFloat(instrument.minTradeAmount)
      : undefined

    const advancedTwapParams = buildAdvancedTwapAlgoParams({
      symbol: item.symbol,
      quantity: effectiveAmount.toString(),
      side: item.side,
      accountId: account.accountId,
      denomination,
      qtyStep,
      minOrderSize,
      raw: {
        durationSeconds: rawValues.advancedTwapDurationSeconds,
        totalSlices: rawValues.advancedTwapTotalSlices,
        bandEnabled: rawValues.advancedTwapBandEnabled,
        bandPct: rawValues.advancedTwapBandPct,
        reduceOnly: rawValues.advancedTwapReduceOnly,
      },
    })

    const advancedTwapSubmissionId = randomUUID()
    await startAdvancedTwap({ ...advancedTwapParams, correlationId: advancedTwapSubmissionId })

    trackOrderItems('advanced_twap', [advancedTwapParams], ctx.getAnalyticsAccounts(), 'order-form')
    const advancedTwapTargets = dedupeTargets([advancedTwapParams])
    const selection = ctx.getSelection()
    ctx.triggerOptimistic(
      advancedTwapSubmissionId,
      advancedTwapTargets,
      selection.instruments,
      selection.options
    )
    ctx.resetFormAfterOrder()
  } catch (error) {
    toast.error(error instanceof Error ? error.message : t('twapSubmissionFailed'), {
      position: 'bottom-right',
    })
    play('error')
    ctx.setIsSubmitting(false)
  }
}
